import type { Vec2 } from '../shared/types';

// Render-time tessellation for the parametric curve entities (ELLIPSE, SPLINE).
// Storing curves parametrically keeps the imported model small; they are sampled
// into points only when drawn or measured.

/** Sample an ellipse (or elliptical arc) into world points. */
export function ellipsePoints(center: Vec2, major: Vec2, ratio: number, start: number, end: number, closed: boolean): Vec2[] {
  let span = closed ? Math.PI * 2 : end - start;
  while (span <= 1e-9) span += Math.PI * 2;
  const mx = major.x, my = major.y;
  const n = Math.max(16, Math.ceil((span / (Math.PI * 2)) * 96));
  const pts: Vec2[] = [];
  for (let i = 0; i <= n; i++) {
    const ang = start + (span * i) / n;
    const c = Math.cos(ang), s = Math.sin(ang);
    // point = centre + cos·major + sin·minor, with minor = perp(major)·ratio
    pts.push({ x: center.x + c * mx - s * my * ratio, y: center.y + c * my + s * mx * ratio });
  }
  return pts;
}

/** A clamped uniform knot vector for n+1 control points of the given degree. */
function clampedKnots(n: number, p: number): number[] {
  const m = n + p + 1;
  const U = new Array<number>(m + 1);
  for (let i = 0; i <= p; i++) U[i] = 0;
  for (let i = p + 1; i <= n; i++) U[i] = i - p;
  for (let i = n + 1; i <= m; i++) U[i] = n - p + 1;
  return U;
}

/** Knot span index containing parameter u (NURBS book A2.1). */
function findSpan(n: number, p: number, u: number, U: number[]): number {
  if (u >= U[n + 1]) return n;
  if (u <= U[p]) return p;
  let low = p, high = n + 1, mid = (low + high) >> 1;
  while (u < U[mid] || u >= U[mid + 1]) {
    if (u < U[mid]) high = mid; else low = mid;
    mid = (low + high) >> 1;
  }
  return mid;
}

/** Evaluate a (non-rational) B-spline at parameter u via De Boor's algorithm. */
function deBoor(u: number, P: Vec2[], p: number, U: number[], n: number): Vec2 {
  const span = findSpan(n, p, u, U);
  const d: Vec2[] = [];
  for (let j = 0; j <= p; j++) d[j] = { x: P[span - p + j].x, y: P[span - p + j].y };
  for (let r = 1; r <= p; r++) {
    for (let j = p; j >= r; j--) {
      const i = span - p + j;
      const denom = U[i + p - r + 1] - U[i];
      const a = denom === 0 ? 0 : (u - U[i]) / denom;
      d[j] = { x: (1 - a) * d[j - 1].x + a * d[j].x, y: (1 - a) * d[j - 1].y + a * d[j].y };
    }
  }
  return d[p];
}

/** Sample a B-spline into world points; falls back to the control polygon when
 *  there are too few control points to define a curve. */
export function splinePoints(control: Vec2[], degree: number, knots: number[] | undefined): Vec2[] {
  const p = degree > 0 ? degree : 3;
  if (control.length <= p) return control.slice();
  const n = control.length - 1;
  let U = knots;
  if (!U || U.length !== n + p + 2) U = clampedKnots(n, p);
  const samples = Math.min(240, Math.max(24, control.length * 8));
  const u0 = U[p], u1 = U[n + 1], out: Vec2[] = [];
  for (let s = 0; s <= samples; s++) {
    const u = u0 + ((u1 - u0) * s) / samples;
    out.push(deBoor(Math.min(u, u1), control, p, U, n));
  }
  return out;
}

/** The tessellated outline of a parametric curve entity, or null for others. */
export function curveTessellation(e: { kind: string } & Record<string, unknown>): Vec2[] | null {
  if (e.kind === 'ellipse') {
    const c = e as unknown as { center: Vec2; major: Vec2; ratio: number; start: number; end: number; closed: boolean };
    return ellipsePoints(c.center, c.major, c.ratio, c.start, c.end, c.closed);
  }
  if (e.kind === 'spline') {
    const s = e as unknown as { control: Vec2[]; degree: number; knots?: number[] };
    return splinePoints(s.control, s.degree, s.knots);
  }
  return null;
}
